import type {
  AcademicIntegrityQuery_EnterpriseSettingsV1Resource_byOrganization_elements_setting_EnterpriseSettingsV1_booleanContentMember as BooleanContentMember,
  AcademicIntegrityQuery_EnterpriseSettingsV1Resource_byOrganization_elements as Setting,
  AcademicIntegrityQuery_EnterpriseSettingsV1Resource_byOrganization_elements_setting_EnterpriseSettingsV1_stringArrayContentMember as StringArrayContentMember,
} from 'bundles/internal-enterprise/components/InternalThirdPartyOrganizationPage/__generated__/AcademicIntegrityQuery';

export const PLAGIARISM_EMAIL_RECIPIENTS = 'PLAGIARISM_EMAIL_RECIPIENTS';

export const PLAGIARISM_DETECTION_ENABLED = 'PLAGIARISM_DETECTION_ENABLED';
export const GRADEBOOK_PLAGIARISM_COLUMNS_ENABLED = 'GRADEBOOK_PLAGIARISM_COLUMNS_ENABLED';
export const DISALLOW_PEER_REVIEW_URL_SHARING = 'DISALLOW_PEER_REVIEW_URL_SHARING';

type EnterpriseSetting =
  | typeof PLAGIARISM_EMAIL_RECIPIENTS
  | typeof PLAGIARISM_DETECTION_ENABLED
  | typeof GRADEBOOK_PLAGIARISM_COLUMNS_ENABLED
  | typeof DISALLOW_PEER_REVIEW_URL_SHARING;

type EnterpriseSettingScope = 'thirdPartyOrganization!' | 'program!';

export const updateEnterpriseSetting = ({
  scope,
  value,
  setting,
  id,
}: {
  scope: EnterpriseSettingScope;
  id?: string;
  setting: EnterpriseSetting;
  value:
    | {
        typeName: 'booleanContent';
        definition: {
          value: boolean;
        };
      }
    | {
        typeName: 'stringArrayContent';
        definition: {
          value: string[];
        };
      };
}) =>
  fetch(`/api/enterpriseSettings.v1/${scope}~${id}~${setting}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      setting: value,
    }),
  });

export const updateOrganizationLevelBooleanSetting = ({
  id,
  setting,
  isEnabled,
}: {
  id: string;
  setting: EnterpriseSetting;
  isEnabled: boolean;
}) =>
  updateEnterpriseSetting({
    scope: 'thirdPartyOrganization!',
    value: {
      typeName: 'booleanContent',
      definition: {
        value: isEnabled,
      },
    },
    id,
    setting,
  });

export const updateOrganizationLevelStringArraySetting = ({
  id,
  setting,
  strings,
}: {
  id: string;
  setting: EnterpriseSetting;
  strings: string[];
}) =>
  updateEnterpriseSetting({
    scope: 'thirdPartyOrganization!',
    value: {
      typeName: 'stringArrayContent',
      definition: {
        value: strings,
      },
    },
    id,
    setting,
  });

export const updateProgramLevelBooleanSetting = ({
  id,
  setting,
  isEnabled,
}: {
  id: string;
  setting: EnterpriseSetting;
  isEnabled: boolean;
}) =>
  updateEnterpriseSetting({
    scope: 'program!',
    value: {
      typeName: 'booleanContent',
      definition: {
        value: isEnabled,
      },
    },
    id,
    setting,
  });

export const getSettingValue = ({
  scope,
  id,
  setting,
  settings,
}: {
  scope: EnterpriseSettingScope;
  id?: string;
  setting: EnterpriseSetting;
  settings: Setting[];
}) =>
  settings?.find((_) => _?.id === [scope, id, setting].join('~'))?.setting as
    | BooleanContentMember
    | StringArrayContentMember
    | undefined;

export const getOrganizationLevelBooleanSettingValue = (
  setting: EnterpriseSetting,
  thirdPartyOrganizationId: string,
  settings: Setting[],
  defaultValue = false
) =>
  (
    getSettingValue({
      scope: 'thirdPartyOrganization!',
      id: thirdPartyOrganizationId,
      setting,
      settings,
    }) as BooleanContentMember
  )?.booleanContent?.value ?? defaultValue;

export const getProgramLevelBooleanSettingValue = (
  programId: string,
  setting: EnterpriseSetting,
  settings: Setting[],
  defaultValue = false
) =>
  (getSettingValue({ scope: 'program!', id: programId, setting, settings }) as BooleanContentMember)?.booleanContent
    ?.value ?? defaultValue;

export const getOrganizationLevelStringArraySettingValue = (
  thirdPartyOrganizationId: string,
  setting: EnterpriseSetting,
  settings: Setting[],
  defaultValue = []
) =>
  (
    getSettingValue({
      scope: 'thirdPartyOrganization!',
      id: thirdPartyOrganizationId,
      setting,
      settings,
    }) as StringArrayContentMember
  )?.stringArrayContent?.value ?? defaultValue;

export const getProgramLevelStringArraySettingValue = (
  programId: string,
  setting: EnterpriseSetting,
  settings: Setting[]
) =>
  (getSettingValue({ scope: 'program!', id: programId, setting, settings }) as StringArrayContentMember)
    ?.stringArrayContent?.value;
